import type { NextPage } from 'next';
import { useSession } from 'next-auth/react';

import { BlockType } from '@web/blocks';

import { ContentBlock } from '@web/components/block-list/content-block';
import { ErrorFrame } from '@web/components/error-frame/error-frame';
import { HeroBlock } from '@web/components/hero-block/hero-block';

import TemplateLayout from '@web/layouts/template/template';

import { useGetUser } from '@web/services/crm-api/hooks';
import { CRMUserType } from '@web/services/crm-api/token';
import { isUserType } from '@web/services/crm-api/user-type.guard';
import { crmError } from '@web/services/errorhandler/appError';
import { UmbracoErrorType } from '@web/services/umbraco/types/basic/ErrorTypes';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';
import { HeroBlockType } from '@web/services/umbraco/types/elements/Hero';

export const amrContentPageContentAlias = 'amrContentPage' as const;

export type AmrContentPageData = UmbracoPage<
	typeof amrContentPageContentAlias,
	{
		hero: HeroBlockType;
		blocks: BlockType[];
	}
>;

const AmrContentPageTemplate: NextPage<{ pageData: AmrContentPageData }> = ({ pageData }) => {
	const { data, error } = useGetUser();
	const { data: session } = useSession();

	if (error) {
		throw crmError({ cause: error, message: 'Failed to fetch user' });
	}

	if (session && data && (!data?.flags?.isArbejdsmarkedRepresentant && !isUserType(session, CRMUserType.EmployedAtDM))) {
		return <ErrorFrame errorType={UmbracoErrorType.Forbidden} />;
	}

	return (
		<TemplateLayout>
			{pageData.properties.hero && <HeroBlock block={pageData.properties.hero} />}
			{pageData.properties.blocks && <ContentBlock blocks={pageData.properties.blocks} />}
		</TemplateLayout>
	);
};

export default AmrContentPageTemplate;
